import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Loader2, MessageSquare, Plus, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useGetEmployerTicketQuery,
  useGetEmployerTicketsQuery,
  useOpenEmployerTicketMutation,
  useReplyEmployerTicketMutation,
} from '@/store/api/apiSlice';
import type { SupportMessage, SupportTicket } from '@/types';

interface EmployerConversationsPanelProps {
  investigationId: string;
  // When true, the panel opens straight into the new-conversation composer
  // (used by the "Message employee" entry point on the investigation page).
  startWithCompose?: boolean;
  // Required when starting a new conversation — the employer needs the
  // supportCode set so the employee can receive replies.
  canCompose?: boolean;
}

type View =
  | { kind: 'list' }
  | { kind: 'thread'; ticketId: string }
  | { kind: 'compose' };

function formatWhen(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readErrorMessage(err: any, fallback = 'Something went wrong'): string {
  return err?.data?.message ?? err?.message ?? fallback;
}

export default function EmployerConversationsPanel({
  investigationId,
  startWithCompose = false,
  canCompose = true,
}: EmployerConversationsPanelProps) {
  const [view, setView] = useState<View>(
    startWithCompose && canCompose ? { kind: 'compose' } : { kind: 'list' },
  );

  // If the parent flips startWithCompose mid-mount (e.g. user clicked "Message
  // employee" while the panel was already showing the list), respect that.
  useEffect(() => {
    if (startWithCompose && canCompose) setView({ kind: 'compose' });
  }, [startWithCompose, canCompose]);

  const { data: tickets = [], isLoading } = useGetEmployerTicketsQuery({ investigationId });

  if (view.kind === 'compose') {
    return (
      <ComposeView
        investigationId={investigationId}
        onCancel={() => setView({ kind: 'list' })}
        onCreated={(t) => setView({ kind: 'thread', ticketId: t.id })}
      />
    );
  }

  if (view.kind === 'thread') {
    return (
      <ThreadView
        ticketId={view.ticketId}
        onBack={() => setView({ kind: 'list' })}
      />
    );
  }

  return (
    <ListView
      tickets={tickets}
      isLoading={isLoading}
      onOpen={(t) => setView({ kind: 'thread', ticketId: t.id })}
      onNew={() => setView({ kind: 'compose' })}
      canCompose={canCompose}
    />
  );
}

// ── List view ────────────────────────────────────────────────────────────────

interface ListViewProps {
  tickets: SupportTicket[];
  isLoading: boolean;
  onOpen: (t: SupportTicket) => void;
  onNew: () => void;
  canCompose: boolean;
}

function ListView({ tickets, isLoading, onOpen, onNew, canCompose }: ListViewProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-[#1b1b1b]">Conversations with the employee</h3>
          <p className="text-xs text-[#717680]">
            Messages here land on the same thread the employee sees, and that admins can see in their inbox.
          </p>
        </div>
        <Button
          type="button"
          onClick={onNew}
          disabled={!canCompose}
          className="bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
          title={canCompose ? undefined : 'The employee must submit their consent form before you can start a conversation.'}
        >
          <Plus className="w-4 h-4" />
          New conversation
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-[#717680]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="border border-dashed border-[#e0e0e0] rounded-lg px-6 py-12 text-center">
          <MessageSquare className="w-6 h-6 text-[#9aa0a6] mx-auto mb-2" />
          <p className="text-sm text-[#1b1b1b] font-medium">No conversations yet</p>
          <p className="text-xs text-[#717680] mt-1">
            Start one to ask the employee for clarification or send instructions.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-[#f0f0f0] border border-[#f0f0f0] rounded-lg overflow-hidden">
          {tickets.map((t) => {
            const last = t.messages?.[t.messages.length - 1];
            const preview = last?.body ?? t.message;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => onOpen(t)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <p className="text-sm font-semibold text-[#1b1b1b] truncate">{t.subject}</p>
                    <span className="text-[11px] text-[#717680] shrink-0">
                      {formatWhen(t.lastMessageAt || t.updatedAt)}
                    </span>
                  </div>
                  <p className="text-xs text-[#4e4d4d] line-clamp-2">{preview}</p>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Thread view ──────────────────────────────────────────────────────────────

interface ThreadViewProps {
  ticketId: string;
  onBack: () => void;
}

function ThreadView({ ticketId, onBack }: ThreadViewProps) {
  const { data: ticket, isLoading } = useGetEmployerTicketQuery(ticketId);
  const [reply, setReply] = useState('');
  const [replyError, setReplyError] = useState('');
  const [sendReply, { isLoading: isSending }] = useReplyEmployerTicketMutation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to the bottom when new messages arrive — feels like a real
  // chat, and matches user expectation for threaded messaging.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [ticket?.messages?.length]);

  const messages = useMemo<SupportMessage[]>(() => {
    if (!ticket) return [];
    // The original ticket body is rendered as the first "employee" or
    // "employer" message so the thread reads naturally. The backend usually
    // includes it as messages[0] already, but we synthesize it if missing.
    if (ticket.messages && ticket.messages.length > 0) return ticket.messages;
    return [
      {
        id: ticket.id,
        senderType: ticket.userId ? 'employer' : 'employee',
        senderName: ticket.user
          ? `${ticket.user.firstName ?? ''} ${ticket.user.lastName ?? ''}`.trim() || null
          : ticket.employeeName ?? null,
        body: ticket.message,
        createdAt: ticket.createdAt,
      },
    ];
  }, [ticket]);

  const handleSend = async () => {
    const trimmed = reply.trim();
    if (!trimmed || isSending) return;
    setReplyError('');
    try {
      await sendReply({ id: ticketId, data: { body: trimmed } }).unwrap();
      setReply('');
    } catch (err) {
      setReplyError(readErrorMessage(err, 'Failed to send reply'));
    }
  };

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm font-medium text-[#1b1b1b] hover:opacity-80"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to conversations
        </button>
        {ticket?.status && (
          <span className="text-[11px] uppercase tracking-wider font-semibold text-[#717680]">
            {ticket.status.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      <div className="border border-[#f0f0f0] rounded-lg flex flex-col bg-white">
        <div className="px-4 py-3 border-b border-[#f0f0f0]">
          <p className="text-sm font-semibold text-[#1b1b1b]">
            {ticket?.subject || (isLoading ? 'Loading…' : 'Conversation')}
          </p>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-h-[420px] min-h-[240px]"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-[#717680]">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-xs text-[#717680] text-center py-6">No messages yet.</p>
          ) : (
            messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
        </div>

        <div className="border-t border-[#f0f0f0] px-4 py-3">
          <textarea
            value={reply}
            onChange={(e) => {
              setReply(e.target.value);
              setReplyError('');
            }}
            placeholder="Write a reply…"
            rows={3}
            className="w-full bg-white border border-[#d5d7da] rounded-md px-3 py-2 text-sm text-[#181d27] placeholder:text-[#9aa0a6] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors resize-y"
          />
          {replyError && (
            <p className="text-xs text-red-600 mt-1">{replyError}</p>
          )}
          <div className="flex justify-end mt-2">
            <Button
              type="button"
              onClick={handleSend}
              disabled={!reply.trim() || isSending}
              className="bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
            >
              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {isSending ? 'Sending…' : 'Send reply'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: SupportMessage }) {
  // Three sender types, three distinct pills. Right-align messages from the
  // current viewer (the employer); left-align everything else.
  const isEmployer = message.senderType === 'employer';
  const pillStyle =
    message.senderType === 'employer'
      ? 'bg-[#FF6B35]/10 text-[#FF6B35]'
      : message.senderType === 'admin'
        ? 'bg-blue-50 text-blue-700'
        : 'bg-gray-100 text-gray-700';
  const pillLabel =
    message.senderType === 'employer'
      ? 'You'
      : message.senderType === 'admin'
        ? 'Admin'
        : 'Employee';

  return (
    <div className={`flex ${isEmployer ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isEmployer ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${pillStyle}`}>
            {pillLabel}
          </span>
          {message.senderName && (
            <span className="text-[11px] text-[#717680]">{message.senderName}</span>
          )}
          <span className="text-[11px] text-[#9aa0a6]">{formatWhen(message.createdAt)}</span>
        </div>
        <div
          className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap leading-relaxed ${
            isEmployer ? 'bg-[#FFF8F4] border border-[#FF6B35]/20' : 'bg-[#fafafa] border border-[#f0f0f0]'
          }`}
        >
          {message.body}
        </div>
      </div>
    </div>
  );
}

// ── Compose view ─────────────────────────────────────────────────────────────

interface ComposeViewProps {
  investigationId: string;
  onCancel: () => void;
  onCreated: (t: SupportTicket) => void;
}

function ComposeView({ investigationId, onCancel, onCreated }: ComposeViewProps) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [open, { isLoading: isCreating }] = useOpenEmployerTicketMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCreating) return;
    const s = subject.trim();
    const m = message.trim();
    if (!s) {
      setError('Subject is required.');
      return;
    }
    if (!m) {
      setError('Message is required.');
      return;
    }
    setError('');
    try {
      const ticket = await open({ investigationId, subject: s, message: m }).unwrap();
      onCreated(ticket);
    } catch (err) {
      setError(readErrorMessage(err, 'Failed to start conversation'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-sm font-medium text-[#1b1b1b] hover:opacity-80"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      <div className="border border-[#f0f0f0] rounded-lg bg-white p-4 flex flex-col gap-3">
        <p className="text-sm font-semibold text-[#1b1b1b]">Start a new conversation</p>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-[#1b1b1b]">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setError('');
            }}
            maxLength={200}
            placeholder="e.g. Confirm address from your last role"
            className="w-full bg-white border border-[#d5d7da] rounded-md h-10 px-3 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-[#1b1b1b]">Message</span>
          <textarea
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setError('');
            }}
            rows={6}
            placeholder="What do you need from the employee?"
            className="w-full bg-white border border-[#d5d7da] rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors resize-y min-h-[120px]"
          />
        </label>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="border-[#d5d7da] text-[#1b1b1b] hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isCreating}
            className="bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2"
          >
            {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isCreating ? 'Starting…' : 'Start conversation'}
          </Button>
        </div>
      </div>
    </form>
  );
}
